/**
 * Short, readable record addresses: `/project/PJ-90F0D6B4/procurement/orders/PO-130` instead of
 * `/procurement/orders/PO-<full uuid>`. The standard is in docs/plans/2026-10-29-url-audit.md (section 7).
 *
 * Everything here is pure: the number lookups are passed in, so the same functions serve the router
 * (src/router/RefRouter.tsx), the tests and any link builder. Inside the app a record is still identified by its uuid and
 * every page keeps parsing `PREFIX-<uuid>`; the router translates at the edge, in both directions:
 *
 *   decode  (address bar -> app):  PO-130 / PO-90F0D6B4 / old PO-<uuid>  ->  PO-<uuid>;  /project/PJ-x/... -> /...
 *   encode  (app -> address bar):  PO-<uuid>  ->  PO-130 (when the record has a number);  /... -> /project/PJ-x/...
 *
 * A number is only unique inside its project, which is why the project sits in the path.
 */
import { ENTITY_PREFIX, type EntityKind } from './entityRoutes';
import { matchAccountRoute } from './accountRoutes';

/** The kinds the API numbers per project (GET /planning/:project_id/record-refs), i.e. the ones a short ref can name. */
export const NUMBERED_KINDS = [
  'quote', 'task', 'payment', 'procurementOrder', 'shipment', 'bundle', 'event', 'venue',
  'teamMember', 'budgetCategory', 'decorElement', 'catalogueItem', 'driver',
] as const satisfies readonly EntityKind[];

export type NumberedKind = (typeof NUMBERED_KINDS)[number];

const KIND_BY_PREFIX: ReadonlyMap<string, NumberedKind> = new Map(NUMBERED_KINDS.map(k => [ENTITY_PREFIX[k], k]));

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HEX8 = /^[0-9a-f]{8}$/i;
const NUMBER = /^[0-9]{1,7}$/;

export type RouteRef =
  | { form: 'number'; kind: NumberedKind; prefix: string; number: number }
  | { form: 'hash8'; kind: NumberedKind; prefix: string; hex: string }
  | { form: 'uuid'; kind: NumberedKind; prefix: string; uuid: string };

/** What a path segment names, or null when it is not a reference of a numbered kind (`new`, a word, `GS-033`, `ST-16A544A1`). */
export function parseRouteRef(segment: string): RouteRef | null {
  const m = /^([A-Za-z]{2})-(.+)$/.exec(segment);
  if (!m) return null;
  const prefix = m[1].toUpperCase();
  const kind = KIND_BY_PREFIX.get(prefix);
  if (!kind) return null;
  const tail = m[2];
  if (UUID.test(tail)) return { form: 'uuid', kind, prefix, uuid: tail.toLowerCase() };
  // 8 characters that are all digits are a hash8, a number has at most 7 digits (same rule as the API).
  if (NUMBER.test(tail)) {
    const number = parseInt(tail, 10);
    return number > 0 ? { form: 'number', kind, prefix, number } : null;
  }
  if (HEX8.test(tail)) return { form: 'hash8', kind, prefix, hex: tail.toLowerCase() };
  return null;
}

/** `PO-130`: the prefix and the unpadded number (labels in tables pad to 3 digits, the address does not). */
export function numberSegment(kind: NumberedKind, number: number): string {
  return `${ENTITY_PREFIX[kind]}-${number}`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Project in the path

export const PROJECT_BASE = '/project';

/** `PJ-90F0D6B4`: a project's reference, the first 8 characters of its uuid. Stable, needs no lookup to build. */
export function projectRef(projectId: string): string {
  return `PJ-${projectId.slice(0, 8).toUpperCase()}`;
}

/** The 8 hex characters of a `PJ-` segment (any case), or null. */
export function parseProjectRef(segment: string): string | null {
  const m = /^PJ-([0-9a-f]{8})$/i.exec(segment);
  return m ? m[1].toLowerCase() : null;
}

/** `/project/PJ-90F0D6B4/budget` -> `{ projectHex: '90f0d6b4', rest: '/budget' }`; any other path is returned as it is. */
export function splitProjectPath(pathname: string): { projectHex: string | null; rest: string } {
  const m = /^\/project\/(PJ-[0-9a-f]{8})(\/.*)?$/i.exec(pathname);
  if (!m) return { projectHex: null, rest: pathname };
  return { projectHex: parseProjectRef(m[1]), rest: m[2] && m[2] !== '' ? m[2] : '/' };
}

/** Pages that belong to the workspace, not to one project, and so never carry the project. */
export function isAccountLevelPath(pathname: string): boolean {
  if (pathname === '/' || pathname === '' || pathname === '/set-password') return true;
  return matchAccountRoute(pathname) !== null;
}

/** The address for an app path: `/budget` -> `/project/PJ-90F0D6B4/budget`. Account pages and prefixed paths are unchanged. */
export function withProjectPath(pathname: string, projectId: string | null | undefined): string {
  if (!projectId || !pathname.startsWith('/') || isAccountLevelPath(pathname)) return pathname;
  if (pathname === PROJECT_BASE || pathname.startsWith(`${PROJECT_BASE}/`)) return pathname;
  return `${PROJECT_BASE}/${projectRef(projectId)}${pathname}`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Record segments

export interface NumberLookup {
  /** uuid of the record with this number in the active project, if known. */
  idByNumber(kind: NumberedKind, number: number): string | undefined;
  /** uuid of the one record starting with these hex characters in the active project, if known. */
  idByHash(kind: NumberedKind, hex: string): string | undefined;
  /** number of this record, if known. */
  numberOf(kind: NumberedKind, uuid: string): number | undefined;
}

export interface UnresolvedRef { segment: string; ref: Extract<RouteRef, { form: 'number' | 'hash8' }> }

/** Bundles are the one kind whose address has no prefix at the moment: `/bundle/<uuid>`. */
function isBundleSlot(segments: readonly string[], i: number): boolean {
  return i > 0 && segments[i - 1] === 'bundle';
}

/** The segment the app expects for this record (`PO-<uuid>`, bare for a bundle). */
function appSegment(ref: RouteRef, uuid: string, bundleSlot: boolean): string {
  return bundleSlot || ref.kind === 'bundle' ? uuid : `${ref.prefix}-${uuid}`;
}

/**
 * Address -> app path. Every `PREFIX-number` and `PREFIX-hash8` segment of a numbered kind becomes the uuid form the pages
 * parse, through the loaded numbers or the resolved answers in `resolved` (segment text -> uuid). What cannot be
 * resolved yet is listed in `unresolved` and the segment is left as it was, so a page shows its own "not found".
 * `PREFIX-uuid` and everything else pass through untouched: every old address is already in the app form.
 */
export function decodeRecordSegments(
  pathname: string,
  lookup: Pick<NumberLookup, 'idByNumber' | 'idByHash'>,
  resolved: ReadonlyMap<string, string> = new Map(),
): { path: string; unresolved: UnresolvedRef[] } {
  const segments = pathname.split('/');
  const unresolved: UnresolvedRef[] = [];
  const out = segments.map((segment, i) => {
    const ref = parseRouteRef(segment);
    if (!ref || ref.form === 'uuid') return segment;
    const uuid = resolved.get(segment.toUpperCase())
      ?? (ref.form === 'number' ? lookup.idByNumber(ref.kind, ref.number) : lookup.idByHash(ref.kind, ref.hex));
    if (uuid) return appSegment(ref, uuid, isBundleSlot(segments, i));
    unresolved.push({ segment, ref });
    return segment;
  });
  return { path: out.join('/'), unresolved };
}

/** App path -> address: `PREFIX-<uuid>` (or the bare uuid after `/bundle/`) becomes `PREFIX-number` when the number is known. */
export function encodeRecordSegments(
  pathname: string,
  numberOf: NumberLookup['numberOf'],
  kinds: ReadonlySet<NumberedKind> = new Set(NUMBERED_KINDS),
): string {
  const segments = pathname.split('/');
  return segments.map((segment, i) => {
    let kind: NumberedKind | undefined;
    let uuid: string | undefined;
    if (isBundleSlot(segments, i) && UUID.test(segment)) { kind = 'bundle'; uuid = segment; }
    else {
      const ref = parseRouteRef(segment);
      if (ref?.form === 'uuid') { kind = ref.kind; uuid = ref.uuid; }
    }
    if (!kind || !uuid || !kinds.has(kind)) return segment;
    const n = numberOf(kind, uuid);
    return n === undefined ? segment : numberSegment(kind, n);
  }).join('/');
}

/** Splits `path?query#hash` so a codec can work on the path alone. */
export function splitHref(href: string): { pathname: string; search: string; hash: string } {
  const hashAt = href.indexOf('#');
  const hash = hashAt === -1 ? '' : href.slice(hashAt);
  const noHash = hashAt === -1 ? href : href.slice(0, hashAt);
  const queryAt = noHash.indexOf('?');
  return queryAt === -1
    ? { pathname: noHash, search: '', hash }
    : { pathname: noHash.slice(0, queryAt), search: noHash.slice(queryAt), hash };
}
